import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  LinearProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeploymentUnitIcon from '@mui/icons-material/DeveloperBoard';
import WarningIcon from '@mui/icons-material/Warning';
import { SettingsDeploymentService } from '../service/settings-deployment.service';
import { useSettingsContext } from '../context/SettingsContext';

interface SettingsDeploymentManagerProps {
  // Optional: Kann erweitert werden für spezifischere Deployment-Auslöser
}

export const SettingsDeploymentManager: React.FC<SettingsDeploymentManagerProps> = () => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deploymentMessage, setDeploymentMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const { hasChanges, markAsSaved } = useSettingsContext();

  // Check deployment status on component mount and periodically
  useEffect(() => {
    checkDeploymentStatus();
    
    // Poll deployment status every 5 seconds if deploying
    const interval = setInterval(() => {
      if (isDeploying) {
        checkDeploymentStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isDeploying]);

  const checkDeploymentStatus = async () => {
    try {
      const status = await SettingsDeploymentService.getDeploymentStatus();
      setIsDeploying(status.isDeploying);
    } catch (error) {
      console.error('Fehler beim Prüfen des Deployment-Status:', error);
    }
  };

  const handleSaveClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    
    try {
      setIsDeploying(true);
      const result = await SettingsDeploymentService.triggerDeployment();
      
      setDeploymentMessage({
        text: result.message,
        type: result.success ? 'success' : 'error'
      });

      if (result.success) {
        markAsSaved(); // Markiere als gespeichert
        // Continue polling for deployment status
        setTimeout(checkDeploymentStatus, 1000);
      } else {
        setIsDeploying(false);
      }
    } catch (error: any) {
      setIsDeploying(false);
      setDeploymentMessage({
        text: error.message || 'Unbekannter Fehler beim Deployment',
        type: 'error'
      });
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
  };

  const handleCloseMessage = () => {
    setDeploymentMessage(null);
  };

  // Nur anzeigen wenn es Änderungen gibt oder gerade ein Deployment läuft
  if (!hasChanges && !isDeploying) {
    return null;
  }

  return (
    <>
      {/* Speicher-Button - nur anzeigen wenn nicht deploying */}
      {!isDeploying && (
        <Button
          variant="contained"
          color="success"
          startIcon={<SaveIcon />}
          onClick={handleSaveClick}
          fullWidth
          sx={{
            fontWeight: 'bold',
            py: 1.5
          }}
        >
          Speichern
        </Button>
      )}

      {/* Deployment-Status */}
      {isDeploying && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              backgroundColor: 'orange.50',
              borderColor: 'warning.main',
              borderWidth: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DeploymentUnitIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                Änderungen werden an die Waschstraße übertragen
              </Typography>
            </Box>
            
            <LinearProgress 
              color="warning" 
              sx={{ 
                mb: 1,
                height: 6,
                borderRadius: 3
              }} 
            />
            
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                textAlign: 'center',
                fontSize: '0.75rem'
              }}
            >
              Bitte warten Sie, während die Änderungen angewendet werden...
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Bestätigungsdialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSave}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
          Einstellungen speichern
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Sind Sie sich sicher, dass Sie die Änderungen speichern möchten? 
            Die neuen Einstellungen werden an die Waschstraße übertragen und sofort aktiv.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleConfirmSave} color="primary" variant="contained" autoFocus>
            Ja, speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast-Benachrichtigungen */}
      <Snackbar
        open={!!deploymentMessage}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseMessage} 
          severity={deploymentMessage?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {deploymentMessage?.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsDeploymentManager; 